(() => {
  if (customElements.get('site-footer')) return;

  class SiteFooter extends HTMLElement {
    constructor() {
      super();
      this.onToggle = this.onToggle.bind(this);
      this.onCategoryClick = this.onCategoryClick.bind(this);
      this._categoriesFetched = false;
    }

    connectedCallback() {
      this.toggleButtons = this.querySelectorAll('[data-action="toggle-column"]');
      this.toggleButtons.forEach((button) => {
        const links = button.nextElementSibling?.nextElementSibling;
        if (links && links.hasAttribute('data-collapsible')) {
          links.setAttribute('aria-hidden', 'true');
        }
        button.addEventListener('click', this.onToggle);
      });

      this.shopLinksContainer = this.querySelector('[data-footer-shop-links]');
      this.collectionHandle = this.dataset.footerCollectionHandle || '';
      this.collectionUrl = this.dataset.footerCollectionUrl || '';

      this.bindCategoryLinks();

      if (this.collectionHandle && !this.isOnCollectionPage()) {
        this.fetchCategories();
      }
    }

    disconnectedCallback() {
      this.toggleButtons?.forEach((btn) => btn.removeEventListener('click', this.onToggle));
      this.unbindCategoryLinks();
    }

    isOnCollectionPage() {
      return /^\/collections\/[^/]+\/?$/.test(window.location.pathname);
    }

    getCollectionBasePath() {
      if (this.collectionUrl) {
        try {
          return new URL(this.collectionUrl, window.location.origin).pathname;
        } catch (_) {}
      }

      const match = window.location.pathname.match(/^\/collections\/([^/]+)/);
      if (match) return `/collections/${match[1]}`;
      return '';
    }

    buildCategoryUrl(link) {
      const collectionPath = this.getCollectionBasePath();
      if (!collectionPath) return link.href;

      const url = new URL(collectionPath, window.location.origin);

      if (link.hasAttribute('data-footer-category-clear')) {
        return url.pathname;
      }

      const paramName = link.dataset.footerCategoryParam;
      const categoryValue = link.dataset.footerCategoryValue;
      if (paramName && categoryValue) {
        url.searchParams.set(paramName, categoryValue);
      }

      return url.pathname + url.search;
    }

    onToggle(event) {
      const button = event.currentTarget;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      const heading = button.nextElementSibling;
      const links = heading?.nextElementSibling;

      if (links && links.hasAttribute('data-collapsible')) {
        button.setAttribute('aria-expanded', String(!isExpanded));
        links.setAttribute('aria-hidden', String(isExpanded));
      }
    }

    bindCategoryLinks() {
      this.categoryLinks = this.querySelectorAll('a[data-footer-category]');
      this.categoryLinks.forEach((link) => link.addEventListener('click', this.onCategoryClick));
    }

    unbindCategoryLinks() {
      this.categoryLinks?.forEach((link) => link.removeEventListener('click', this.onCategoryClick));
    }

    onCategoryClick(event) {
      const link = event.currentTarget;
      if (!(link instanceof HTMLAnchorElement)) return;

      event.preventDefault();
      const targetUrl = this.buildCategoryUrl(link);

      if (this.isOnCollectionPage()) {
        const absoluteUrl = new URL(targetUrl, window.location.origin).href;
        document.dispatchEvent(
          new CustomEvent('collection:navigate', {
            detail: { url: absoluteUrl, scrollToGrid: true },
          })
        );
        return;
      }

      window.location.assign(targetUrl + '#collection-product-grid');
    }

    async fetchCategories() {
      if (this._categoriesFetched || !this.shopLinksContainer || !this.collectionHandle) return;
      this._categoriesFetched = true;

      const root = window.Shopify?.routes?.root ?? '/';
      const sectionId = this.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '') || 'footer';
      const fetchUrl = `${root}collections/${encodeURIComponent(this.collectionHandle)}?sections=${sectionId}`;

      try {
        const response = await fetch(fetchUrl);
        if (!response.ok) return;

        const data = await response.json();
        const html = data[sectionId];
        if (!html) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newLinks = doc.querySelector('[data-footer-shop-links]');
        if (!newLinks || !newLinks.children.length) return;

        this.unbindCategoryLinks();
        this.shopLinksContainer.innerHTML = newLinks.innerHTML;
        this.bindCategoryLinks();
      } catch (_) {}
    }
  }

  customElements.define('site-footer', SiteFooter);
})();
